// frontend/src/components/government/LevyWaiverSubmissionModal.js
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Building2,
  User,
  Receipt,
  Plane,
  Stethoscope,
  Copy,
  Info,
  AlertTriangle,
} from 'lucide-react';
import toast from '../../utils/toast';
import { staffAPI } from '../../services/api';
import LoadingSpinner from '../UI/LoadingSpinner';
import Modal from '../UI/Modal';
import { StatusBadge } from '../UI/StatusBadge';
import { formatDate } from '../../utils/dateUtils';
import { formatLeaveDays, pluralizeDays } from '../../utils/numberUtils';
import { copyToClipboard } from '../../utils/clipboard';

const formatSGD = (value) =>
  value == null
    ? null
    : new Intl.NumberFormat('en-SG', { style: 'currency', currency: 'SGD' }).format(value);

// claim_status -> StatusBadge status + label (mirrors the tracker list)
const STATUS_META = {
  to_claim: { badge: 'pending', label: 'To Claim' },
  pending: { badge: 'pending', label: 'Draft' },
  submitted: { badge: 'submitted', label: 'Submitted to MOM' },
  approved: { badge: 'completed', label: 'Waived' },
  rejected: { badge: 'rejected', label: 'Rejected' },
  cancelled: { badge: 'pending', label: 'Cancelled' },
  not_eligible: { badge: 'pending', label: 'Not Eligible' },
};

// Full set of MOM levy-waiver reason categories, shown for HR reference. The
// tracker auto-derives overseas leave / hospitalisation from the leave type.
const MOM_REASON_CATEGORIES = [
  'Overseas leave',
  'Failed to return from overseas leave',
  'Hospitalisation leave',
  'Police / embassy custody',
  'Death',
  'Harbour-craft / vessel leave',
  'Malaysian National Service',
];

const PERIOD_SOURCE_LABEL = {
  flight_ticket: 'Round-trip flight ticket (departure + return)',
  flight_departure_only: 'One-way flight ticket (departure only; return on re-entry)',
  leave_dates: 'Leave dates (flight ticket not yet captured)',
};

const handleCopy = async (value, label) => {
  const ok = await copyToClipboard(value);
  if (ok) toast.success(`${label} copied`);
  else toast.error('Copy failed');
};

// One label/value row. `copyable` adds a copy-to-clipboard button for the
// fields HR transcribes into WP Online (FIN, dates, amounts, references).
const Field = ({ label, value, copyable = false, copyValue, mono = false }) => {
  const isEmpty = value == null || value === '';
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-gray-50 last:border-0">
      <span className="text-xs text-gray-500 flex-shrink-0 w-44 pt-0.5">{label}</span>
      <div className="flex items-center gap-2 min-w-0 flex-1 justify-end">
        <span
          className={`text-sm text-right break-words ${
            isEmpty ? 'text-gray-400 italic' : 'text-gray-900'
          } ${mono && !isEmpty ? 'font-mono tracking-tight' : ''}`}
        >
          {isEmpty ? 'Not on file' : value}
        </span>
        {copyable && !isEmpty && (
          <button
            type="button"
            onClick={() => handleCopy(copyValue ?? value, label)}
            className="flex-shrink-0 p-1 text-gray-400 hover:text-red-600 rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1"
            title={`Copy ${label}`}
          >
            <Copy className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
};

const Section = ({ icon: Icon, title, children }) => (
  <div className="rounded-xl border border-gray-200 overflow-hidden">
    <div className="bg-gray-50 px-4 py-2.5 flex items-center gap-2 border-b border-gray-200">
      <Icon className="w-4 h-4 text-red-600" />
      <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
    </div>
    <div className="px-4 py-1">{children}</div>
  </div>
);

const buildCopyAllText = (packet) => {
  const { employer, worker, levy_bill: bill, waiver } = packet;
  const d = (v) => (v ? formatDate(v) : '-');
  const lines = [
    'MOM Foreign Worker Levy Waiver — Submission Details',
    '',
    '[Employer / Entity]',
    `Company entity: ${employer.company_entity || '-'}`,
    `UEN: ${employer.company_uen || '-'}`,
    `Levy account no.: ${employer.levy_account_number || '(fill from WP Online)'}`,
    `Corppass submitter: ${employer.corppass_submitter || '(authorised submitter)'}`,
    `eService: ${employer.eservice_channel || '-'}`,
    '',
    '[Worker / Pass Holder]',
    `Name: ${worker.full_name || '-'}`,
    `FIN: ${worker.fin_number || '-'}`,
    `Work pass type: ${worker.work_pass_type || '-'}`,
    `Work permit no.: ${worker.work_permit_number || '-'}`,
    `Pass status: ${worker.pass_status || '-'}${
      worker.pass_expiry_date ? ` (expires ${d(worker.pass_expiry_date)})` : ''
    }`,
    '',
    '[Levy Bill]',
    `Bill month / period: ${bill.bill_month || '-'}`,
    `Daily levy rate: ${formatSGD(bill.daily_rate) || '-'}`,
    `Monthly levy rate: ${formatSGD(bill.monthly_rate) || '-'}`,
    '',
    '[Waiver]',
    `Reason category: ${waiver.reason_label || '-'}`,
    `Start date (left Singapore): ${d(waiver.start_date)}`,
    `End date (returns to Singapore): ${d(waiver.end_date)}${
      waiver.return_date_pending ? ' (return pending — confirm on re-entry)' : ''
    }`,
    `Total days: ${formatLeaveDays(waiver.total_days)}`,
    `Waivable days: ${formatLeaveDays(waiver.eligible_days)}`,
    `Est. levy waived: ${formatSGD(waiver.estimated_amount) || '-'}`,
    `Earliest submission: ${d(waiver.application_window_start)}${
      waiver.submission_window_open ? ' (open)' : ` (${waiver.window_note || 'not open yet'})`
    }`,
    `Apply by: ${d(waiver.application_deadline)}`,
  ];
  return lines.join('\n');
};

const LevyWaiverSubmissionModal = ({ leaveApplicationId, onClose }) => {
  const isOpen = !!leaveApplicationId;

  const { data: packet, isLoading, isError } = useQuery({
    queryKey: ['levy-waiver-submission', leaveApplicationId],
    queryFn: async () => {
      const res = await staffAPI.getLevyWaiverSubmissionDetail(leaveApplicationId);
      return res.data;
    },
    enabled: isOpen,
    staleTime: 60 * 1000,
  });

  const employer = packet?.employer || {};
  const worker = packet?.worker || {};
  const bill = packet?.levy_bill || {};
  const waiver = packet?.waiver || {};
  const isOverseas = waiver.reason_code === 'overseas_leave';
  const ReasonIcon = isOverseas ? Plane : Stethoscope;

  const deadlineValue = waiver.application_deadline ? (
    <span className={waiver.is_overdue ? 'text-red-600 font-medium' : ''}>
      {formatDate(waiver.application_deadline)}
      {waiver.is_overdue && ' (overdue)'}
      {!waiver.is_overdue && typeof waiver.days_until_deadline === 'number' && (
        <span className="block text-xs text-gray-400">
          {waiver.days_until_deadline} day(s) left
        </span>
      )}
    </span>
  ) : null;

  const earliestValue = waiver.application_window_start ? (
    <span className={waiver.submission_window_open ? 'text-green-600' : 'text-amber-600'}>
      {formatDate(waiver.application_window_start)}
      <span className="block text-[11px]">
        {waiver.submission_window_open
          ? 'Window open — ready to submit'
          : waiver.window_note || 'Not open yet'}
      </span>
    </span>
  ) : null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Levy Waiver Submission Details"
      size="lg"
      theme="admin"
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-admin-outline">
            Close
          </button>
          {packet && (
            <button
              type="button"
              onClick={() => handleCopy(buildCopyAllText(packet), 'Submission details')}
              className="btn btn-admin-primary"
            >
              <Copy className="w-4 h-4" />
              Copy all
            </button>
          )}
        </>
      }
    >
      {isLoading ? (
        <div className="py-10">
          <LoadingSpinner />
        </div>
      ) : isError || !packet ? (
        <div className="py-10 text-center">
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <p className="text-sm text-gray-600">
            Could not load levy-waiver submission details for this leave application.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Context banner */}
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 flex gap-2">
            <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-blue-900">
              WP Online / EP eService isn&apos;t connected to this HR portal. Use the details
              below to lodge the levy waiver manually — tap the copy icon on any field, or{' '}
              <strong>Copy all</strong> for the full packet.
            </p>
          </div>

          {/* Header summary */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-gray-900">{worker.full_name}</span>
            <span className="text-sm text-gray-500">({worker.staff_id})</span>
            <span className="text-gray-300">·</span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-sky-50 text-sky-700">
              <ReasonIcon className="w-3 h-3" />
              {waiver.reason_label}
            </span>
            <StatusBadge
              status={STATUS_META[waiver.claim_status]?.badge || 'pending'}
              label={STATUS_META[waiver.claim_status]?.label}
            />
          </div>

          <Section icon={Building2} title="Employer / Entity">
            <Field label="Company entity" value={employer.company_entity} copyable />
            <Field label="UEN" value={employer.company_uen} copyable mono />
            <Field
              label="Levy account no."
              value={employer.levy_account_number}
              copyable
              mono
            />
            <Field label="Corppass submitter" value={employer.corppass_submitter} />
            <Field label="eService channel" value={employer.eservice_channel} />
          </Section>

          <Section icon={User} title="Worker / Pass Holder">
            <Field label="Worker name" value={worker.full_name} copyable />
            <Field label="FIN" value={worker.fin_number} copyable mono />
            <Field label="Work pass type" value={worker.work_pass_type} />
            <Field label="Work permit no." value={worker.work_permit_number} copyable mono />
            <Field
              label="Pass status"
              value={
                worker.pass_status ? (
                  <span
                    className={worker.pass_status === 'Expired' ? 'text-red-600 font-medium' : ''}
                  >
                    {worker.pass_status}
                    {worker.pass_expiry_date && (
                      <span className="block text-xs text-gray-400">
                        expires {formatDate(worker.pass_expiry_date)}
                      </span>
                    )}
                  </span>
                ) : null
              }
            />
            <Field label="Department" value={worker.department} />
          </Section>

          <Section icon={Receipt} title="Levy Bill (period being claimed)">
            <Field
              label="Bill month / period"
              value={
                bill.bill_month ? (
                  <span>
                    {bill.bill_month}
                    {bill.amount_is_estimated && (
                      <span className="block text-[11px] text-amber-600">
                        no bill uploaded for this month — estimated from latest
                      </span>
                    )}
                  </span>
                ) : null
              }
              copyable
              copyValue={bill.bill_month}
            />
            <Field
              label="Daily levy rate"
              value={bill.daily_rate ? `${formatSGD(bill.daily_rate)}/day` : null}
              mono
            />
            <Field
              label="Monthly levy rate"
              value={bill.monthly_rate ? formatSGD(bill.monthly_rate) : null}
              mono
            />
            <Field
              label="Levy billed (month)"
              value={bill.levy_amount_billed ? formatSGD(bill.levy_amount_billed) : null}
              mono
            />
          </Section>

          <Section icon={ReasonIcon} title="Waiver Reason & Period">
            <Field label="Reason category" value={waiver.reason_label} />
            <Field label="Leave type" value={waiver.leave_type_name} />
            <Field
              label="Start date (left Singapore)"
              value={waiver.start_date ? formatDate(waiver.start_date) : null}
              copyable
              copyValue={waiver.start_date ? formatDate(waiver.start_date) : ''}
            />
            <Field
              label="End date (returns to Singapore)"
              value={
                waiver.end_date ? (
                  <span className={waiver.return_date_pending ? 'text-amber-600' : ''}>
                    {formatDate(waiver.end_date)}
                    {waiver.return_date_pending && (
                      <span className="block text-[11px] text-amber-600">
                        provisional — confirm actual re-entry date before lodging
                      </span>
                    )}
                  </span>
                ) : null
              }
              copyable
              copyValue={waiver.end_date ? formatDate(waiver.end_date) : ''}
            />
            {isOverseas && (
              <Field label="Dates sourced from" value={PERIOD_SOURCE_LABEL[waiver.period_source]} />
            )}
            <Field
              label="Total days"
              value={`${formatLeaveDays(waiver.total_days)} ${pluralizeDays(waiver.total_days)}`}
            />
            <Field
              label="Waivable days"
              value={
                waiver.is_eligible ? (
                  <span>
                    {formatLeaveDays(waiver.eligible_days)} {pluralizeDays(waiver.eligible_days)}
                    {waiver.annual_cap_capped && (
                      <span className="block text-[11px] text-amber-600">capped at 60/yr</span>
                    )}
                  </span>
                ) : (
                  <span className="text-amber-600">
                    {isOverseas ? 'Under 7 consecutive days' : 'Not eligible'}
                  </span>
                )
              }
            />
            {waiver.flight_booking_reference && (
              <Field label="Flight booking ref." value={waiver.flight_booking_reference} copyable mono />
            )}
            <Field label="Est. levy waived" value={formatSGD(waiver.estimated_amount)} mono />
            <Field
              label="Annual cap used"
              value={
                typeof waiver.annual_cap_used === 'number' ? (
                  <span>
                    {formatLeaveDays(waiver.annual_cap_used)} / 60 day(s)
                    {typeof waiver.annual_cap_remaining === 'number' && (
                      <span className="block text-[11px] text-gray-400">
                        {formatLeaveDays(waiver.annual_cap_remaining)} day(s) remaining this year
                        {waiver.annual_cap_used_prior > 0 &&
                          ` · ${formatLeaveDays(waiver.annual_cap_used_prior)} from earlier leave`}
                      </span>
                    )}
                  </span>
                ) : null
              }
            />
            <Field label="Status" value={STATUS_META[waiver.claim_status]?.label} />
            <Field label="Waiver reference" value={waiver.waiver_reference_number} copyable />
            <Field label="MOM reference" value={waiver.mom_reference_number} copyable />
            <Field
              label="Submitted on"
              value={waiver.submitted_date ? formatDate(waiver.submitted_date) : null}
            />
            <Field label="Earliest submission" value={earliestValue} />
            <Field label="Apply by (deadline)" value={deadlineValue} />
          </Section>

          <div className="rounded-xl bg-gray-50 border border-gray-100 p-3">
            <p className="text-[11px] font-medium text-gray-500 mb-1">
              MOM levy-waiver reason categories
            </p>
            <div className="flex flex-wrap gap-1.5">
              {MOM_REASON_CATEGORIES.map((r) => {
                const active = r.toLowerCase().startsWith(
                  (waiver.reason_label || '').toLowerCase().slice(0, 8),
                );
                return (
                  <span
                    key={r}
                    className={`text-[11px] px-2 py-0.5 rounded-full ${
                      active ? 'bg-sky-100 text-sky-700 font-medium' : 'bg-white text-gray-500 border border-gray-200'
                    }`}
                  >
                    {r}
                  </span>
                );
              })}
            </div>
          </div>

          <p className="text-[11px] text-gray-400 px-1">
            Worker FIN / pass type are read from the MOM levy bill and confidential HR records
            (HR-admin only). The levy account number and Corppass submitter aren&apos;t held in
            this portal — fill them from WP Online. Start/End dates follow MOM&apos;s overseas-leave
            rule (date left Singapore → date returns to Singapore); the amount is an estimate from
            the billed daily levy rate, and MOM confirms the exact waiver.
          </p>
        </div>
      )}
    </Modal>
  );
};

export default LevyWaiverSubmissionModal;
